import React, { useEffect, useRef, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { observer } from 'mobx-react';

import { Loading } from 'core';
import { useStore } from 'core/store';
import Section from 'core/components/section';
import { Card } from 'core/components/card';
import ValueField from 'core/components/value-field';
import Label from 'core/components/label/label';
import { useQueryParams } from 'core/hooks/useQueryParams';
import LabelWithCopyButton from 'core/components/label-with-copy-button';
import { brandConstants } from 'brand-config';

import useStyles from './agent-details.styles';

const getFullName = (user) => {
  if (user?.attributes?.given_name || user?.attributes?.family_name) {
    return `${user?.attributes?.given_name || ''} ${user?.attributes?.family_name || ''}`;
  }
  return undefined;
};

const AgentDetails = () => {
  const classes = useStyles();
  const { account, search, affinityLookups } = useStore();
  const { policies } = account;
  const recentPolicy = policies.getMostRecentActiveOrFuturePolicy();

  const customerAffinityCode = recentPolicy?.segments?.[0]?.global.affinity;
  const affinityData = affinityLookups.data.find(({ affinity }) => affinity === customerAffinityCode);
  const initialProducerCognitoUsername = recentPolicy?.salesRep;
  const soldProducer = recentPolicy?.bindRep;
  const initialLeadSource = recentPolicy?.offer?.quote?.leadSource;
  const isAgentSold = customerAffinityCode && affinityData?.isAgency;
  const affinityInfo = isAgentSold && affinityData;
  const agencyInfo = affinityLookups.agencyInformation[affinityData?.data?.informationId || customerAffinityCode];
  const serviceAgreement = affinityData?.data?.serviceAgreement;

  const [agentCognitoUser, setAgentCognitoUser] = useState();
  const [loading, setLoading] = useState(true);
  const [searchingStaffUsers, setSearchingStaffUsers] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const queryParams = useQueryParams();
  const agentDetailsElement = useRef(null);

  useEffect(() => {
    const isLoading = account.accountLoading || !account.id || searchingStaffUsers || affinityLookups.partialLoading;
    setLoading(isLoading);
  }, [
    account.accountLoading,
    account.id,
    searchingStaffUsers,
    affinityLookups,
    affinityLookups.partialLoading,
    customerAffinityCode,
    agencyInfo
  ]);

  useEffect(() => {
    if (queryParams.scroll === 'agentDetails' && !scrolled && !loading && agentDetailsElement.current !== null) {
      agentDetailsElement.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setScrolled(true);
    }
  }, [loading, scrolled, queryParams]);

  useEffect(() => {
    if (customerAffinityCode && !agencyInfo && !affinityLookups.partialLoading) {
      affinityLookups.getAgencyInformation(affinityData?.data?.informationId || customerAffinityCode);
    }
  }, [affinityLookups, isAgentSold, affinityData, customerAffinityCode, agencyInfo]);

  useEffect(() => {
    if (initialProducerCognitoUsername && isAgentSold) {
      // isEffectActive variable guards against stale writes when the user navigates between accounts
      // mid-fetch: the cleanup flips this to false so a slow/in-flight lookup
      // for the previous policy can't land after a newer effect has already
      // started, which would otherwise show the prior account's agent.
      // The MeiliSearch client doesn't expose abort here, so we ignore the
      // late response instead of cancelling it.
      let isEffectActive = true;
      const getAgentDataFromSearchCognitoIndex = async () => {
        setSearchingStaffUsers(true);
        const user = await search.searchStaffUsers({ username: initialProducerCognitoUsername });
        if (isEffectActive) {
          setAgentCognitoUser(user);
          setSearchingStaffUsers(false);
        }
      };

      getAgentDataFromSearchCognitoIndex();
      return () => {
        isEffectActive = false;
        // Reset loading flag here too — if cleanup fires mid-fetch, the guarded
        // setSearchingStaffUsers(false) above is skipped and the spinner sticks.
        setSearchingStaffUsers(false);
      };
      // Explicit else: the if branch returns the effect's cleanup, so falling
      // through would unconditionally clear agentCognitoUser on every effect
      // run — including agent-sold policies — causing a flash of empty state
      // before the fetch resolves.
      // biome-ignore lint/style/noUselessElse: see comment above
    } else {
      // Clear stale agent data when navigating to a policy without an agency-sold producer,
      // otherwise the previously loaded account's agent details persist in local state.
      setAgentCognitoUser(undefined);
    }
  }, [initialProducerCognitoUsername, isAgentSold, search]);

  if (!recentPolicy) {
    return null;
  }

  return (
    <div ref={agentDetailsElement}>
      <Section title="Agent Details">
        {loading ? (
          <Loading />
        ) : (
          <Card id={'agentDetails'}>
            <Grid container className={classes.card}>
              {isAgentSold && agentCognitoUser && (
                <Grid item xs={12}>
                  <Label type="darkGreenSmallBold"> Agent </Label>
                </Grid>
              )}
              <Grid item xs={6}>
                <ValueField label="Primary Producer" value={initialProducerCognitoUsername || 'N/A'} />
              </Grid>
              <Grid item xs={6}>
                <ValueField label="Sold Producer" value={soldProducer || 'N/A'} />
              </Grid>
              {isAgentSold && agentCognitoUser && (
                <>
                  <Grid item xs={12}>
                    <ValueField label="Agent Full Name" value={getFullName(agentCognitoUser) || 'N/A'} />
                  </Grid>
                  <Grid item xs={12} className={classes.separator}>
                    <Label type="infoLabel" className={classes.copyButtonLabel}>
                      Agent Email
                    </Label>
                    <LabelWithCopyButton type="infoValue">
                      {agentCognitoUser?.attributes?.email || 'N/A'}
                    </LabelWithCopyButton>
                  </Grid>
                  <Label type="darkGreenSmallBold"> Agency </Label>
                  <Grid item xs={12}>
                    <ValueField label="Agency Name" value={agencyInfo?.name || affinityInfo?.name || 'N/A'} />
                  </Grid>
                  <Grid item xs={12}>
                    <Label type="infoLabel" className={classes.copyButtonLabel}>
                      Agency Email
                    </Label>
                    <LabelWithCopyButton type="infoValue">
                      {agencyInfo?.email || affinityInfo?.ccEmail || 'N/A'}
                    </LabelWithCopyButton>
                  </Grid>
                  <Grid item xs={6}>
                    <ValueField label="Serviced By" value={serviceAgreement ? brandConstants.SHORT_NAME : 'Agency'} />
                  </Grid>
                  {!serviceAgreement && (
                    <Grid item xs={12}>
                      <Label type="infoLabel" className={classes.copyButtonLabel}>
                        Agency Service Phone Number
                      </Label>
                      <LabelWithCopyButton type="infoValue">
                        {agencyInfo?.servicing_phone_number || 'N/A'}
                      </LabelWithCopyButton>
                    </Grid>
                  )}
                  <Grid item xs={12} className={classes.separator}>
                    <Label type="infoLabel" className={classes.copyButtonLabel}>
                      Agency Phone Number
                    </Label>
                    <LabelWithCopyButton type="infoValue">
                      {agencyInfo?.phone || affinityInfo?.phone || 'N/A'}
                    </LabelWithCopyButton>
                  </Grid>
                </>
              )}
              {isAgentSold && agentCognitoUser && (
                <Grid item xs={12}>
                  <Label type="darkGreenSmallBold"> Additional Information </Label>
                </Grid>
              )}
              <Grid item xs={6}>
                <ValueField label="Initial Lead Source" value={initialLeadSource || 'N/A'} />
              </Grid>
              <Grid item xs={6}>
                <ValueField label="Initial Affinity Code" value={customerAffinityCode || 'N/A'} />
              </Grid>
              {!isAgentSold && (
                <Grid item xs={6}>
                  <ValueField label="Serviced By" value={brandConstants.SHORT_NAME} />
                </Grid>
              )}
            </Grid>
          </Card>
        )}
      </Section>
    </div>
  );
};

export default observer(AgentDetails);
